/**
 * Générateur — « Connaitre le sens et les situations d'emploi de ces opérations. »
 * Attendu ID : 900 | Niveau : 5ème | Domaine : Nombres et calculs / Opérations
 *
 * Mis en conformité avec la spécification 900.json (campagne 5e, 2026-09-21).
 * La spécification est la seule référence ; ce qu'elle ne dit pas est noté
 * dans le rapport audit/5eme/900.md (section « Non tranché »).
 *
 * ─── Plan de cartes ────────────────────────────────────────────────────────
 *
 *   Carte « calcul_nu » — Calculer avec les quatre opérations (niveaux 1 et 2)
 *     Formats : texte entiers (niveau 1 : calcul nu, entiers, division exacte),
 *     texte décimaux (niveau 2 : décimaux à une ou deux décimales, dont, une
 *     fois sur deux pour la multiplication, un facteur inférieur à 1).
 *     Réponse exacte, jamais arrondie (valeurs en centièmes, produits en
 *     millièmes ou dix-millièmes, tout en entiers).
 *
 *   Carte « choisir_operation » — Choisir la bonne opération (niveau 3)
 *     Formats : qcm_unique (4 expressions : les trois autres opérations sont
 *     les distracteurs, jamais des valeurs), texte (le résultat), expression
 *     (l'élève ÉCRIT le calcul, comparé comme expression, liste finie des
 *     écritures admises), tableau colonnesLibres (3 situations × 4 opérations).
 *     NEUF familles : deux par opération plus une multiplication par un
 *     facteur inférieur à 1 (tissu). Structures variées : réunion, comparaison
 *     (« de plus que », « de moins que »), retrait, itération, partage,
 *     groupement. Le résultat tombe juste par construction (on tire le
 *     résultat et un opérande, l'autre s'en déduit ; centimes et dixièmes en
 *     entiers, jamais de flottant).
 *
 *   Carte « associer_calcul » — Associer chaque calcul à son énoncé (niveau 5)
 *     Formats : relier (4 situations ↔ 4 calculs sur les MÊMES nombres x et y,
 *     plus deux cibles leurres « y − x » et « y ÷ x »), qcm_unique (une
 *     situation, les 4 calculs), texte (le résultat).
 *     Les mêmes données servent aux quatre opérations : seule la question
 *     posée décide du calcul. Construction : y ∈ [2 ; 12], x = k × y avec
 *     k ≥ 2, x ≤ 200, et jamais (y = 2, k = 2) où x − y = x ÷ y.
 *
 *   Carte « question_resoluble » — Quelle question peut-on résoudre ? (niveau 3)
 *     Formats : qcm_unique (3 questions, une seule résoluble), texte (la
 *     réponse). Trois familles à UNE étape : peinture (V ÷ n), trajet
 *     (5 × d), lecture (P ÷ p, p ≤ 12). Le rang de longueur de la bonne
 *     réponse est tiré (voir genQuestionResolubleQcm).
 *
 *   Carte « division_reste » — Division avec reste (niveau 4)
 *     Formats : texte_trou_saisie (quotient ET reste, chacun nommé dans la
 *     situation), qcm_unique (une question d'interprétation : quotient,
 *     quotient + 1 ou reste, distracteurs codés), texte (une question, réponse
 *     exacte). Dividende ≤ 200, diviseur ∈ [2 ; 12], reste ∈ [1 ; d − 1],
 *     quotient ≥ 2.
 *
 * ─── Spécification (900.json) ──────────────────────────────────────────────
 *
 *   Bornes appliquées :
 *     - nombres du calcul dans [2 ; 200], entiers ou décimaux à 1 ou 2 décimales ;
 *     - soustraction : second terme strictement inférieur au premier ;
 *     - multiplication : second facteur ≤ 12 ;
 *     - division : diviseur entier dans [2 ; 12] ; reste dans [0 ; d − 1] ;
 *     - registre : entiers (niveau 1), décimaux (niveau 2 et situations).
 *   Exclusions appliquées : aucun diviseur nul ni égal à 1, aucun facteur 1,
 *     aucune différence nulle ni négative ; aucun double / triple / moitié /
 *     quart / dixième / carré / cube / opposé / inverse ; dans un tableau ou un
 *     relier, jamais deux questions de mêmes valeurs.
 *   Erreurs visées → mots-clés (codes des distracteurs `_meta.params.distracteurs[].code`,
 *     `contenu.cibles_leurres[].code`, ou `_meta.params.erreurVisee`) :
 *     1. « Choisir l'opération d'après un mot-clé… »          → mot_cle_trompeur
 *     2. « Traiter un partage comme une soustraction… »       → partage_confondu_soustraction
 *     3. « Croire qu'une division tombe toujours juste… »     → division_toujours_exacte
 *     4. « La multiplication agrandit toujours… »             → multiplication_agrandit_toujours
 *     5. « Inverser les deux termes d'une soustraction… »     → inversion_des_termes
 *     6. « Donner le quotient sans dire ce qu'il représente » → quotient_non_interprete
 *     Un distracteur imposé par `type_reponse` (une des trois autres opérations)
 *     qui ne matérialise aucune de ces erreurs porte le code `autre_operation`.
 *   Graduation → cartes :
 *     niveau 1 → calcul_nu_entiers_texte ; niveau 2 → calcul_nu_decimaux_texte ;
 *     niveau 3 → choisir_operation (4 formats), question_resoluble (2 formats) ;
 *     niveau 4 → division_reste (3 formats) ; niveau 5 → associer_calcul (3 formats).
 *   Non tranché (détail dans le rapport) :
 *     - `variables.*_nombre_du_calcul.min = 2` contredit `registre_des_nombres`,
 *       l'erreur visée 4 et le niveau 2 (« un facteur décimal inférieur à 1 ») :
 *       le facteur inférieur à 1 est tiré dans [0,2 ; 0,95], signalé par
 *       `_meta.params.facteurInf1`, les autres nombres restent dans [2 ; 200] ;
 *     - les variables de SÉRIE (part de situations ≥ 1/2, 4 à 12 questions,
 *       une division sur quatre avec reste) ne se règlent pas dans un gen qui
 *       produit une question à la fois : 12 formats sur 14 posent une
 *       situation, la division avec reste a sa propre carte ;
 *     - la spec est muette sur le format « quelle question peut-on
 *       résoudre » : conservé, ses leurres (questions non résolubles) ne
 *       servent aucune erreur visée.
 *
 * ─── Conventions transverses ───────────────────────────────────────────────
 *   - `NB` (U+00A0) devant CHAQUE unité, y compris dans les choix de QCM et les
 *     lignes de tableau.
 *   - Le drapeau calculatrice vit dans `payload.contenu` (`_meta` est strippé au
 *     POST). Accordé aux saisies de situations × et ÷ sur décimaux ; refusé sur
 *     le calcul nu (c'est le calcul qui est demandé) et sur les entiers.
 *   - Le tableau titre ses colonnes avec les SYMBOLES (+ − × ÷) : les noms
 *     d'opérations débordaient sur mobile (retour utilisateur, 2026-08-02).
 *   - Coups de pouce : `src/generators/coupsDePouce/900.js`, une fiche par carte
 *     conservée (calcul_nu et division_reste n'en ont pas encore).
 *   - Saisie libre : une réponse juste accompagnée de son unité (« 9,95 € ») est
 *     comptée fausse (`memeValeurNumerique` ne strippe que les espaces) ;
 *     chantier global, hors périmètre.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, makeRelier, tex,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 900;

const L_NU    = 'Calculer avec les quatre opérations';
const L_CHOIX = 'Choisir la bonne opération';
const L_ASSOC = 'Associer chaque calcul à son énoncé';
const L_QRES  = 'Quelle question peut-on résoudre ?';
const L_RESTE = 'Division avec reste : quotient et reste dans la situation';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Espace insécable : jamais de nombre séparé de son unité en fin de ligne. */
const NB = ' ';

const OPS4 = ['+', '−', '×', '÷'];
const TEX_OP = { '+': '+', '−': '-', '×': '\\times', '÷': '\\div' };

/** Calculatrice autorisée — dans `contenu`, seul canal qui survit au POST. */
const avecCalculatrice = (payload) => {
    payload.contenu = { ...payload.contenu, calculatrice: true };
    return payload;
};

/** Paramètres de tirage exposés au harnais et au test de conformité (jamais persistés). */
const avecParams = (payload, params) => {
    payload._meta.params = params;
    return payload;
};

const PRENOMS_F = ['Alice', 'Inès', 'Maya', 'Sofia', 'Léa', 'Jade'];
const PRENOMS_M = ['Kamel', 'Lucas', 'Nolan', 'Rayan', 'Adam', 'Tom'];
const PRENOMS = [...PRENOMS_F, ...PRENOMS_M];

/** Entier `n` à l'échelle 10^k → écriture française sans zéro inutile. */
const scaleToFr = (n, k) => {
    if (k === 0) return String(n);
    const s = String(n).padStart(k + 1, '0');
    const ent = s.slice(0, -k);
    const dec = s.slice(-k).replace(/0+$/, '');
    return dec ? `${ent},${dec}` : ent;
};
const centToFr = (c) => scaleToFr(c, 2);
const dixToFr = (d) => scaleToFr(d, 1);
/** Écriture française → nombre JS (pour `_meta.params` uniquement). */
const valeur = (s) => Number(String(s).replace(',', '.'));
/** Décimal en KaTeX : la virgule entre accolades, sinon KaTeX insère une espace. */
const texNb = (s) => String(s).replace(',', '{,}');

/**
 * Décimal NON entier en centièmes dans [minCent ; maxCent], à une décimale
 * (une fois sur deux) ou deux. Boucle bornée, repli déterministe vérifié.
 */
const tirerDecimalCent = (minCent, maxCent) => {
    const uneDecimale = Math.random() < 0.5;
    for (let essai = 0; essai < 60; essai++) {
        let c = randInt(minCent, maxCent);
        if (uneDecimale) c -= c % 10;
        if (c >= minCent && c % 100 !== 0 && (uneDecimale || c % 10 !== 0)) return c;
    }
    const repli = minCent + 50;                      // ex. 2,50
    if (repli <= maxCent && repli % 100 !== 0) return repli;
    throw new Error(`tirerDecimalCent : aucun décimal dans [${minCent} ; ${maxCent}]`);
};

/** Décimal NON entier en dixièmes dans [minDix ; maxDix] (une décimale). */
const tirerDecimalDix = (minDix, maxDix) => {
    for (let essai = 0; essai < 60; essai++) {
        const d = randInt(minDix, maxDix);
        if (d % 10 !== 0) return d;
    }
    const repli = minDix + 5;                        // ex. 2,5
    if (repli <= maxDix && repli % 10 !== 0) return repli;
    throw new Error(`tirerDecimalDix : aucun décimal dans [${minDix} ; ${maxDix}]`);
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Calcul nu (niveaux 1 et 2)
// ═══════════════════════════════════════════════════════════

/** Niveau 1 : entiers, les quatre opérations, division exacte. */
const tirerCalculNuEntiers = () => {
    const op = randomChoice(OPS4);
    if (op === '+') {
        const a = randInt(2, 200), b = randInt(2, 200);
        return { op, a, b, res: a + b };
    }
    if (op === '−') {
        const a = randInt(3, 200), b = randInt(2, a - 1);   // différence ≥ 1
        return { op, a, b, res: a - b };
    }
    if (op === '×') {
        const a = randInt(2, 200), b = randInt(2, 12);
        return { op, a, b, res: a * b };
    }
    const b = randInt(2, 12);
    const q = randInt(2, Math.floor(200 / b));
    return { op, a: b * q, b, res: q };
};

const genCalculNuEntiers = () => {
    const { op, a, b, res } = tirerCalculNuEntiers();
    const texte = `<p>Calcule : ${tex(`${a} ${TEX_OP[op]} ${b}`)}</p><p>Écris le résultat.</p>`;
    const payload = makeSaisie(String(res), texte, opts(L_NU));
    return avecParams(payload, { niveau: 1, op, nombres: [a, b] });
};

/**
 * Niveau 2 : décimaux à une ou deux décimales. Tout est calculé en entiers à
 * une échelle connue (`k` décimales), le résultat est EXACT.
 * Multiplication : une fois sur deux, le second facteur est un décimal
 * inférieur à 1 (0,2 à 0,95, jamais 0,5 ni 0,25 : « moitié » et « quart »
 * sont exclus par la spec) — `facteurInf1` le signale.
 */
const FACTEURS_INF_1 = [20, 30, 35, 40, 45, 60, 65, 70, 75, 80, 85, 90, 95]; // centièmes

const tirerCalculNuDecimaux = () => {
    const op = randomChoice(OPS4);
    if (op === '+') {
        const a = tirerDecimalCent(200, 20000);
        const b = Math.random() < 0.5 ? tirerDecimalCent(200, 20000) : randInt(2, 200) * 100;
        return { op, a: [a, 2], b: [b, 2], res: [a + b, 2], facteurInf1: false };
    }
    if (op === '−') {
        const a = tirerDecimalCent(410, 20000);          // ≥ 4,10 : place pour b ≥ 2 non entier
        const b = tirerDecimalCent(200, a - 1);
        return { op, a: [a, 2], b: [b, 2], res: [a - b, 2], facteurInf1: false };
    }
    if (op === '×') {
        if (Math.random() < 0.5) {
            // facteur < 1 : premier facteur entier ou à une décimale
            const a = Math.random() < 0.5 ? [randInt(2, 200), 0] : [tirerDecimalDix(21, 1999), 1];
            const f = randomChoice(FACTEURS_INF_1);
            const fk = f % 10 === 0 ? [f / 10, 1] : [f, 2];
            return { op, a, b: fk, res: [a[0] * fk[0], a[1] + fk[1]], facteurInf1: true };
        }
        const a = [tirerDecimalCent(200, 20000), 2];
        const b = Math.random() < 0.5 ? [randInt(2, 12), 0] : [tirerDecimalDix(21, 119), 1];
        return { op, a, b, res: [a[0] * b[0], a[1] + b[1]], facteurInf1: false };
    }
    const d = randInt(2, 12);
    const q = tirerDecimalCent(200, Math.floor(20000 / d));
    return { op, a: [d * q, 2], b: [d, 0], res: [q, 2], facteurInf1: false };
};

const genCalculNuDecimaux = () => {
    const t = tirerCalculNuDecimaux();
    const fa = scaleToFr(t.a[0], t.a[1]);
    const fb = scaleToFr(t.b[0], t.b[1]);
    const res = scaleToFr(t.res[0], t.res[1]);
    const texte = `<p>Calcule : ${tex(`${texNb(fa)} ${TEX_OP[t.op]} ${texNb(fb)}`)}</p>` +
        '<p>Écris le résultat exact en écriture décimale (ex. : 12,5).</p>';
    const payload = makeSaisie(res, texte, opts(L_NU));
    return avecParams(payload, {
        niveau: 2, op: t.op, nombres: [valeur(fa), valeur(fb)], facteurInf1: t.facteurInf1,
        ...(t.facteurInf1 ? { erreurVisee: 'multiplication_agrandit_toujours' } : {}),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Choisir la bonne opération (niveau 3)
// ═══════════════════════════════════════════════════════════

/**
 * Chaque famille tire le RÉSULTAT et un opérande, l'autre opérande s'en
 * déduit — le calcul tombe juste par construction. Valeurs en centimes (€),
 * dixièmes ou centièmes d'unité selon la famille. Tous les nombres du calcul
 * sont dans [2 ; 200] (spec), sauf le facteur < 1 de la famille « tissu ».
 *
 * `codes` : pour chacune des trois autres opérations, l'erreur visée que le
 * distracteur matérialise (`autre_operation` = imposé par la spec sans erreur
 * visée associée).
 */
const FAMILLES_OPERATION = [
    {
        id: 'plus_que', structure: 'comparaison',
        tirer: () => {
            const res = randInt(300, 1500) + randomChoice([5, 15, 25, 45, 65, 85]); // l'autre, centimes
            const b = randInt(200, 900) + randomChoice([5, 20, 40, 60, 80]);        // écart ≥ 2,05
            return { a: res + b, b, res };
        },
        enonce: ({ a, b }, qui, autre) =>
            `${qui} a ${centToFr(a)}${NB}€ dans sa tirelire, soit ${centToFr(b)}${NB}€ de plus que ${autre}. ` +
            `Combien d'argent a ${autre} ?`,
        fmt: centToFr, op: '−', suffixe: '€',
        codes: { '+': 'mot_cle_trompeur', '÷': 'partage_confondu_soustraction', '×': 'autre_operation' },
    },
    {
        id: 'reste', structure: 'retrait',
        tirer: () => {
            const res = randInt(200, 600) + randomChoice([5, 15, 25, 45, 65, 85]);  // repas, centimes
            const b = randInt(200, 900) + randomChoice([5, 20, 40, 60, 80]);        // solde restant ≥ 2,05
            return { a: res + b, b, res };
        },
        enonce: ({ a, b }, qui) =>
            `${qui} avait ${centToFr(a)}${NB}€ sur sa carte de cantine. ` +
            `Après le repas, il reste ${centToFr(b)}${NB}€ sur la carte. Combien a coûté le repas ?`,
        fmt: centToFr, op: '−', suffixe: '€',
        codes: { '÷': 'partage_confondu_soustraction', '+': 'autre_operation', '×': 'autre_operation' },
    },
    {
        id: 'decouper', structure: 'partage',
        tirer: () => {
            const n = randInt(3, 8);
            const res = randInt(20, 99) + randomChoice([1, 3, 7, 9]);  // morceau, mm (2,1 à 10,8 cm)
            return { a: n * res, b: n, res };
        },
        enonce: ({ a, b }, qui) =>
            `${qui} doit découper ${dixToFr(a)}${NB}cm de ruban en ${b} morceaux de même longueur. ` +
            'Quelle est la longueur d\'un morceau ?',
        // b est un COMPTE de morceaux (entier), pas des dixièmes → fmtB dédié
        fmt: dixToFr, fmtB: String, op: '÷', suffixe: 'cm',
        codes: { '−': 'partage_confondu_soustraction', '+': 'autre_operation', '×': 'autre_operation' },
    },
    {
        id: 'bouquets', structure: 'groupement',
        tirer: () => {
            const b = randInt(2, 12);                     // roses par bouquet
            const res = randInt(2, Math.min(15, Math.floor(200 / b)));
            return { a: b * res, b, res };
        },
        enonce: ({ a, b }, qui) =>
            `${qui} a ${a} roses et compose des bouquets de ${b} roses chacun. ` +
            'Combien de bouquets peut-on composer ?',
        fmt: String, op: '÷', suffixe: 'bouquets',
        codes: { '−': 'partage_confondu_soustraction', '+': 'autre_operation', '×': 'autre_operation' },
    },
    {
        id: 'prix_kg', structure: 'iteration',
        tirer: () => {
            const a = randInt(21, 49);   // masse en dixièmes de kg (2,1 à 4,9 kg)
            const b = randInt(21, 119);  // prix en dixièmes d'€ le kg (2,1 à 11,9 €)
            return { a, b, res: a * b };  // res en centièmes d'€
        },
        enonce: ({ a, b }, qui) =>
            `${qui} achète ${dixToFr(a)}${NB}kg de pommes à ${dixToFr(b)}${NB}€ le kilogramme. ` +
            'Quel est le prix payé ?',
        fmt: dixToFr, fmtRes: centToFr, op: '×', suffixe: '€',
        codes: { '+': 'autre_operation', '−': 'autre_operation', '÷': 'autre_operation' },
    },
    {
        id: 'briques', structure: 'iteration',
        tirer: () => {
            const b = randInt(4, 9);                     // compte de briques
            const a = randInt(21, 99);                   // masse d'une brique, dixièmes de kg
            return { a, b, res: a * b };
        },
        enonce: ({ a, b }) =>
            `Une brique pèse ${dixToFr(a)}${NB}kg. Combien pèsent ${b} briques identiques ?`,
        fmt: dixToFr, fmtB: String, op: '×', suffixe: 'kg',
        codes: { '+': 'autre_operation', '−': 'autre_operation', '÷': 'autre_operation' },
    },
    {
        id: 'tissu', structure: 'iteration', facteurInf1: true,
        tirer: () => {
            const a = randInt(21, 399);                  // prix du mètre, dixièmes d'€ (2,1 à 39,9 €)
            const b = randomChoice([3, 4, 6, 7, 8, 9]);  // longueur en dixièmes de m (< 1 m)
            return { a, b, res: a * b };                 // centimes
        },
        enonce: ({ a, b }, qui) =>
            `Un tissu coûte ${dixToFr(a)}${NB}€ le mètre. ${qui} en achète ${dixToFr(b)}${NB}m. ` +
            `Quel est le montant payé par ${qui} ?`,
        fmt: dixToFr, fmtRes: centToFr, op: '×', suffixe: '€',
        codes: { '÷': 'multiplication_agrandit_toujours', '+': 'autre_operation', '−': 'autre_operation' },
    },
    {
        id: 'reunion', structure: 'reunion',
        tirer: () => {
            const a = randInt(25, 350) + randomChoice([0, 1, 3, 7]);   // km, dixièmes
            const b = randInt(25, 350) + randomChoice([0, 2, 4, 9]);
            return { a, b, res: a + b };
        },
        enonce: ({ a, b }, qui) =>
            `${qui} parcourt ${dixToFr(a)}${NB}km à vélo le matin et ${dixToFr(b)}${NB}km l'après-midi. ` +
            'Quelle distance a été parcourue dans la journée ?',
        fmt: dixToFr, op: '+', suffixe: 'km',
        codes: { '−': 'autre_operation', '×': 'autre_operation', '÷': 'autre_operation' },
    },
    {
        id: 'moins_que_prix', structure: 'comparaison',
        tirer: () => {
            const a = randInt(800, 2400) + randomChoice([5, 15, 45, 75, 95]);  // livre payé, centimes
            const b = randInt(200, 700) + randomChoice([5, 20, 40, 60, 80]);   // écart ≥ 2,05
            return { a, b, res: a + b };
        },
        enonce: ({ a, b }, qui, autre) =>
            `${qui} a payé son livre ${centToFr(a)}${NB}€, soit ${centToFr(b)}${NB}€ de moins que ${autre}. ` +
            `Quel est le prix du livre de ${autre} ?`,
        fmt: centToFr, op: '+', suffixe: '€',
        codes: { '−': 'mot_cle_trompeur', '×': 'autre_operation', '÷': 'autre_operation' },
    },
];

const tirerOperation = () => {
    const fam = randomChoice(FAMILLES_OPERATION);
    const t = fam.tirer();
    const qui = randomChoice(PRENOMS);
    const autre = randomChoice(PRENOMS.filter(p => p !== qui));
    const fa = fam.fmt(t.a), fb = (fam.fmtB || fam.fmt)(t.b);
    return { fam, t, qui, autre, fa, fb };
};

const paramsOperation = ({ fam, fa, fb }, extra = {}) => ({
    niveau: 3, famille: fam.id, structure: fam.structure, op: fam.op,
    nombres: [valeur(fa), valeur(fb)], facteurInf1: fam.facteurInf1 === true, ...extra,
});

const genChoixOperationQcm = () => {
    const tirage = tirerOperation();
    const { fam, t, qui, autre, fa, fb } = tirage;
    const bonne = `${fa} ${fam.op} ${fb}`;
    const autresOps = OPS4.filter(op => op !== fam.op);
    const choix = [bonne, ...autresOps.map(op => `${fa} ${op} ${fb}`)];
    const texte = `<p>${fam.enonce(t, qui, autre)}</p>` +
        '<p>Quel calcul permet de répondre à la question ?</p>';
    const payload = makeQcm(choix, bonne, texte, opts(L_CHOIX));
    return avecParams(payload, paramsOperation(tirage, {
        distracteurs: autresOps.map(op => ({ valeur: `${fa} ${op} ${fb}`, code: fam.codes[op] })),
    }));
};

const genChoixOperationSaisie = () => {
    const tirage = tirerOperation();
    const { fam, t, qui, autre } = tirage;
    const reponse = (fam.fmtRes || fam.fmt)(t.res);
    const texte = `<p>${fam.enonce(t, qui, autre)}</p>` +
        '<p>Écris ta réponse en écriture décimale si besoin (ex. : 12,5).</p>';
    const payload = makeSaisie(reponse, texte, { ...opts(L_CHOIX), suffixe: fam.suffixe });
    // Les familles + et − tirent l'automatisme « additionner et soustraire des
    // décimaux » (17,65 + 4,20) : pas de calculatrice. × et ÷ n'y sont pas.
    const final = (fam.op === '×' || fam.op === '÷') ? avecCalculatrice(payload) : payload;
    return avecParams(final, paramsOperation(tirage));
};

/**
 * Les écritures admises du calcul (comparaison d'expression : espaces
 * retirés, × ≡ *, − ≡ -, virgule ≡ point par `normaliserExpression`). Liste
 * FINIE : les deux ordres pour + et ×, un seul pour − et ÷ (l'ordre inverse
 * est précisément l'erreur visée « inversion des termes ») ; ÷ s'écrit aussi
 * « / » ou « : » ; × aussi « x » ; un décimal à une décimale s'accepte avec
 * un zéro final (« 12,50 »).
 */
const ecrituresExpression = (fa, fb, op) => {
    const variantes = (s) => (s.includes(',') && s.split(',')[1].length === 1 ? [s, `${s}0`] : [s]);
    const out = [];
    const push = (s) => { if (!out.includes(s)) out.push(s); };
    variantes(fa).forEach(a => variantes(fb).forEach(b => {
        if (op === '+') { push(`${a} + ${b}`); push(`${b} + ${a}`); }
        if (op === '−') { push(`${a} − ${b}`); }
        if (op === '×') { push(`${a} × ${b}`); push(`${b} × ${a}`); push(`${a} x ${b}`); push(`${b} x ${a}`); }
        if (op === '÷') { push(`${a} ÷ ${b}`); push(`${a} / ${b}`); push(`${a} : ${b}`); }
    }));
    return out;
};

const genChoixOperationExpression = () => {
    const tirage = tirerOperation();
    const { fam, t, qui, autre, fa, fb } = tirage;
    const ecritures = ecrituresExpression(fa, fb, fam.op);
    const bonne = ecritures[0];
    const texte = `<p>${fam.enonce(t, qui, autre)}</p>` +
        '<p>Écris le calcul qui permet de répondre à la question, sans le résultat, ' +
        'avec les nombres de l\'énoncé et le signe + , −, × (ou *) ou ÷ (ou /). Exemple : 12,5 / 5</p>';
    const payload = makeSaisie(bonne, texte, {
        ...opts(L_CHOIX), comparaison: 'expression', reponses_acceptees: ecritures.slice(1),
    });
    return avecParams(payload, paramsOperation(tirage, {
        ...(fam.op === '−' || fam.op === '÷' ? { erreurVisee: 'inversion_des_termes' } : {}),
    }));
};

const genChoixOperationTableau = () => {
    // 3 familles DISTINCTES → lignes distinctes (jamais deux questions de mêmes
    // valeurs) ; les OPÉRATIONS, elles, peuvent se répéter (9 familles pour
    // 4 colonnes) : plus de déduction par élimination.
    const fams = shuffle(FAMILLES_OPERATION).slice(0, 3);
    const colonnes = OPS4;
    const lignes = [];
    const bonnesReponses = [];
    const lignesParams = [];
    fams.forEach(fam => {
        const t = fam.tirer();
        const qui = randomChoice(PRENOMS);
        const autre = randomChoice(PRENOMS.filter(p => p !== qui));
        const fa = fam.fmt(t.a), fb = (fam.fmtB || fam.fmt)(t.b);
        lignes.push(fam.enonce(t, qui, autre));
        bonnesReponses.push(colonnes.indexOf(fam.op));
        lignesParams.push(paramsOperation({ fam, fa, fb }));
    });
    const br = lignes.map((l, i) => `[${l}|${colonnes[bonnesReponses[i]]}]`).join('');
    const texte = '<p>Pour chaque situation, coche l\'opération qui permet de répondre à la question.</p>';
    const payload = {
        type: 'qcm',
        texte,
        contenu: { choix: [], bonne_reponse: br },
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'table', config: { type: 'colonnesLibres', colonnes, lignes, bonnesReponses } }],
        },
        niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID,
        _meta: { label: L_CHOIX },
    };
    return avecParams(payload, { niveau: 3, lignes: lignesParams });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Associer chaque calcul à son énoncé (niveau 5)
// ═══════════════════════════════════════════════════════════

/**
 * Mêmes nombres x et y pour les quatre opérations : y ∈ [2 ; 12] (facteur et
 * diviseur), x = k × y ≤ 200 avec k ≥ 2. Les quatre résultats x + y, x − y,
 * x × y et x ÷ y = k sont deux à deux distincts par construction, à l'unique
 * exception (y = 2, k = 2) où x − y = x ÷ y = 2 : exclue.
 */
const tirerAssocier = () => {
    for (let essai = 0; essai < 40; essai++) {
        const y = randInt(2, 12);
        const k = randInt(2, Math.floor(200 / y));
        if (y === 2 && k === 2) continue;
        return { x: k * y, y, k };
    }
    return { x: 36, y: 4, k: 9 };   // repli : 40, 32, 144, 9 distincts
};

const THEMES_ASSOC = [
    { objet: 'billes', contenant: 'sachets', contenantSing: 'sachet' },
    { objet: 'cartes', contenant: 'paquets', contenantSing: 'paquet' },
    { objet: 'photos', contenant: 'albums', contenantSing: 'album' },
];

/** Une structure par opération, deux formulations chacune ; `codes` comme en carte 2. */
const STRUCTURES_ASSOC = [
    {
        op: '+', structure: 'reunion',
        enonces: [
            (x, y, p, th) => `${p} a ${x} ${th.objet} et en reçoit ${y}. Combien de ${th.objet} a ${p} maintenant ?`,
            (x, y, p, th) => `${p} possède ${x} ${th.objet} et en achète ${y} de plus. Combien de ${th.objet} cela fait-il en tout ?`,
        ],
        res: (x, y) => x + y,
        codes: { '−': 'autre_operation', '×': 'autre_operation', '÷': 'autre_operation' },
    },
    {
        op: '−', structure: 'retrait_ou_comparaison',
        enonces: [
            (x, y, p, th) => `${p} a ${x} ${th.objet} et en donne ${y}. Combien de ${th.objet} reste-t-il à ${p} ?`,
            (x, y, p, th) => `${p} a ${x} ${th.objet}, soit ${y} ${th.objet} de plus que Tom. Combien de ${th.objet} a Tom ?`,
        ],
        res: (x, y) => x - y,
        codes: { '+': 'mot_cle_trompeur', '÷': 'partage_confondu_soustraction', '×': 'autre_operation' },
    },
    {
        op: '×', structure: 'iteration',
        enonces: [
            (x, y, p, th) => `${p} a ${y} ${th.contenant} de ${x} ${th.objet} chacun. Combien de ${th.objet} cela fait-il en tout ?`,
            (x, y, p, th) => `Chaque ${th.contenantSing} contient ${x} ${th.objet} et ${p} en a ${y}. Combien de ${th.objet} a ${p} ?`,
        ],
        res: (x, y) => x * y,
        codes: { '+': 'autre_operation', '−': 'autre_operation', '÷': 'autre_operation' },
    },
    {
        op: '÷', structure: 'partage_ou_groupement',
        enonces: [
            (x, y, p, th) => `${p} range ${x} ${th.objet} dans ${y} ${th.contenant} identiques. Combien de ${th.objet} dans chaque ${th.contenantSing} ?`,
            (x, y, p, th) => `${p} range ${x} ${th.objet} par ${th.contenant} de ${y}. Combien de ${th.contenant} sont remplis ?`,
        ],
        res: (x, y) => x / y,
        codes: { '−': 'partage_confondu_soustraction', '+': 'autre_operation', '×': 'autre_operation' },
    },
];

const calcAssoc = (x, y, op) => `${x} ${op} ${y}`;

const genAssocierRelier = () => {
    const { x, y } = tirerAssocier();
    const th = randomChoice(THEMES_ASSOC);
    const p = randomChoice(PRENOMS.filter(n => n !== 'Tom'));
    const pairs = STRUCTURES_ASSOC.map(s => ({
        source: randomChoice(s.enonces)(x, y, p, th),
        target: calcAssoc(x, y, s.op),
    }));
    const texte = '<p>Les quatre situations utilisent les mêmes nombres. ' +
        'Relie chaque situation au calcul qui permet de répondre à sa question. Deux calculs ne servent à rien.</p>';
    const payload = makeRelier(pairs, texte, {
        ...opts(L_ASSOC),
        cibles_leurres: [
            { target: `${y} − ${x}`, code: 'inversion_des_termes' },
            { target: `${y} ÷ ${x}`, code: 'inversion_des_termes' },
        ],
    });
    return avecParams(payload, { niveau: 5, nombres: [x, y] });
};

const genAssocierQcm = () => {
    const { x, y } = tirerAssocier();
    const th = randomChoice(THEMES_ASSOC);
    const p = randomChoice(PRENOMS.filter(n => n !== 'Tom'));
    const s = randomChoice(STRUCTURES_ASSOC);
    const bonne = calcAssoc(x, y, s.op);
    const autresOps = OPS4.filter(op => op !== s.op);
    const texte = `<p>${randomChoice(s.enonces)(x, y, p, th)}</p>` +
        '<p>Quel calcul répond à la question posée ?</p>';
    const payload = makeQcm([bonne, ...autresOps.map(op => calcAssoc(x, y, op))], bonne, texte, opts(L_ASSOC));
    return avecParams(payload, {
        niveau: 5, op: s.op, structure: s.structure, nombres: [x, y],
        distracteurs: autresOps.map(op => ({ valeur: calcAssoc(x, y, op), code: s.codes[op] })),
    });
};

const genAssocierSaisie = () => {
    const { x, y } = tirerAssocier();
    const th = randomChoice(THEMES_ASSOC);
    const p = randomChoice(PRENOMS.filter(n => n !== 'Tom'));
    const s = randomChoice(STRUCTURES_ASSOC);
    const texte = `<p>${randomChoice(s.enonces)(x, y, p, th)}</p><p>Écris le nombre demandé.</p>`;
    const payload = makeSaisie(String(s.res(x, y)), texte, opts(L_ASSOC));
    return avecParams(payload, { niveau: 5, op: s.op, structure: s.structure, nombres: [x, y] });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Quelle question peut-on résoudre ? (niveau 3)
// ═══════════════════════════════════════════════════════════

/**
 * Une seule question est résoluble PAR STRUCTURE : les autres portent sur une
 * grandeur jamais fournie (masse, prix, durée, horaire).
 *
 * Le QCM tire le RANG DE LONGUEUR visé, puis choisit les deux leurres qui le
 * réalisent (cf. `genQuestionResolubleQcm`) — sinon la bonne réponse était la
 * plus longue des trois 5 000 fois sur 5 000. `nonResolubles` doit garder au
 * moins deux entrées plus courtes ET deux plus longues que `resolubles()[1]` ;
 * un filet retombe sur un tirage libre si une réécriture rompt l'équilibre.
 *
 * Chaque famille est un calcul à UNE étape : V ÷ n, 5 × d, P ÷ p (p ≤ 12).
 */
const FAMILLES_QRES = [
    {
        id: 'peinture', op: '÷',
        tirer: () => {
            const n = randInt(3, 8);
            const v = randInt(12, 98);                    // dixièmes de L par pot
            return { n, v, V: n * v };
        },
        enonce: ({ n, V }) =>
            `On répartit équitablement ${dixToFr(V)}${NB}L de peinture dans ${n} pots identiques.`,
        resolubles: () => [
            'Quel volume contient un pot ?',
            'Quel volume de peinture contient un pot ?',
            'Quel volume de peinture chaque pot contient-il une fois rempli ?',
        ],
        nonResolubles: () => [
            'Combien pèse un pot ?',
            'Combien coûte la peinture ?',
            'Quelle est la masse totale de peinture ?',
            'Quelle surface peut-on peindre avec un seul pot ?',
            'Combien de temps faut-il pour remplir tous les pots ?',
        ],
        nombres: ({ V, n }) => [valeur(dixToFr(V)), n],
        reponse: ({ v }) => dixToFr(v), suffixe: 'L',
    },
    {
        id: 'trajet', op: '×',
        tirer: () => ({ d: randInt(52, 399) }),           // dixièmes de km (5,2 à 39,9)
        enonce: ({ d }) =>
            `Louise parcourt ${dixToFr(d)}${NB}km chaque jour pour aller à son travail, cinq jours par semaine. ` +
            `Elle travaille de 8${NB}h${NB}30 à 17${NB}h${NB}30.`,
        resolubles: () => [
            'Quelle distance en une semaine ?',
            'Quelle distance parcourt-elle en une semaine ?',
            'Quelle distance Louise parcourt-elle chaque semaine pour aller au travail ?',
        ],
        nonResolubles: () => [
            'Quelle est la durée du trajet ?',
            'Combien dépense-t-elle en carburant ?',
            'À quelle heure part-elle de chez elle ?',
            'À quelle heure arrive-t-elle chez elle le soir ?',
            'Quel est le prix de son abonnement de transport ?',
        ],
        nombres: ({ d }) => [valeur(dixToFr(d)), 5],   // d km × 5 jours : le multiplicateur ≤ 12
        reponse: ({ d }) => dixToFr(5 * d),
        suffixe: 'km',
    },
    {
        id: 'lecture', op: '÷',
        tirer: () => {
            const p = randInt(2, 12);                     // pages par soir : diviseur ≤ 12
            const j = randInt(4, 16);                     // P ≤ 192
            return { p, j, P: p * j };
        },
        enonce: ({ P, p }) =>
            `Un livre compte ${P} pages. Chaque soir, Naïm lit exactement ${p} pages.`,
        resolubles: () => [
            'En combien de soirs le livre sera-t-il fini ?',
            'En combien de soirs Naïm finira-t-il son livre ?',
            'Combien de soirs faudra-t-il à Naïm pour terminer son livre ?',
        ],
        nonResolubles: () => [
            'Combien coûte ce livre ?',
            'Combien de mots contient ce livre ?',
            'Combien de chapitres compte ce livre ?',
            'À quelle date Naïm aura-t-il terminé la lecture du livre ?',
            'Combien de temps Naïm consacre-t-il à la lecture chaque soir ?',
        ],
        nombres: ({ P, p }) => [P, p],
        reponse: ({ j }) => String(j), suffixe: 'soirs',
    },
];

const genQuestionResolubleQcm = () => {
    const fam = randomChoice(FAMILLES_QRES);
    const t = fam.tirer();
    const bonne = fam.resolubles()[1];              // formulation de référence
    const pool = fam.nonResolubles();
    const courts = pool.filter(q => q.length < bonne.length);
    const longs = pool.filter(q => q.length > bonne.length);
    // 0 = la bonne est la plus longue, 2 = la plus courte. Tiré uniformément.
    const rang = randInt(0, 2);
    let leurres;
    if (rang === 0) leurres = shuffle(courts).slice(0, 2);
    else if (rang === 2) leurres = shuffle(longs).slice(0, 2);
    else leurres = [randomChoice(courts), randomChoice(longs)];
    if (leurres.length < 2 || leurres.some(q => !q)) leurres = shuffle(pool).slice(0, 2);
    const texte = `<p>${fam.enonce(t)}</p>` +
        '<p>Quelle question peut-on résoudre avec les données de l\'énoncé ?</p>';
    const payload = makeQcm([bonne, ...leurres], bonne, texte, opts(L_QRES));
    return avecParams(payload, {
        niveau: 3, famille: fam.id, op: fam.op, nombres: fam.nombres(t),
        leurres: 'questions non résolubles (aucune erreur visée de la spec)',
    });
};

const genQuestionResolubleSaisie = () => {
    const fam = randomChoice(FAMILLES_QRES);
    const t = fam.tirer();
    const texte = `<p>${fam.enonce(t)}</p>` +
        `<p>${randomChoice(fam.resolubles())} Écris ta réponse en écriture décimale si besoin (ex. : 12,5).</p>`;
    const payload = makeSaisie(fam.reponse(t), texte, { ...opts(L_QRES), suffixe: fam.suffixe });
    return avecParams(avecCalculatrice(payload), { niveau: 3, famille: fam.id, op: fam.op, nombres: fam.nombres(t) });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — Division avec reste (niveau 4)
// ═══════════════════════════════════════════════════════════

/**
 * Dividende N = d × q + r avec d ∈ [2 ; 12], r ∈ [1 ; d − 1], q ≥ 2, N ≤ 200.
 * Chaque contexte nomme ce qu'on attend (le quotient, le quotient + 1 pour
 * « tout ranger », ou le reste) : sans cela deux réponses seraient défendables.
 * Une seule question par famille et par clé.
 */
const CONTEXTES_RESTE = [
    {
        id: 'bouquets',
        enonce: (N, d, p) => `${p} a ${N} roses et compose des bouquets de ${d} roses chacun.`,
        libQ: 'Bouquets complets', libR: 'Roses restantes',
        questions: [
            { cle: 'q', texte: 'Combien de bouquets complets peut-on composer ?', suffixe: 'bouquets' },
            { cle: 'r', texte: 'Combien de roses restent sans bouquet ?', suffixe: 'roses' },
        ],
    },
    {
        id: 'minibus',
        enonce: (N, d) => `${N} élèves partent en sortie. Chaque minibus transporte au maximum ${d} élèves.`,
        libQ: 'Minibus complets', libR: 'Élèves dans le dernier minibus',
        questions: [
            { cle: 'q1', texte: 'Combien de minibus faut-il pour emmener tous les élèves ?', suffixe: 'minibus' },
            { cle: 'q', texte: 'Combien de minibus seront complets ?', suffixe: 'minibus' },
            { cle: 'r', texte: 'Combien d\'élèves monteront dans le minibus incomplet ?', suffixe: 'élèves' },
        ],
    },
    {
        id: 'bonbons',
        enonce: (N, d, p) => `${p} partage équitablement ${N} bonbons entre ${d} enfants.`,
        libQ: 'Bonbons par enfant', libR: 'Bonbons restants',
        questions: [
            { cle: 'q', texte: 'Combien de bonbons reçoit chaque enfant ?', suffixe: 'bonbons' },
            { cle: 'r', texte: 'Combien de bonbons restent après le partage ?', suffixe: 'bonbons' },
        ],
    },
    {
        id: 'oeufs',
        enonce: (N, d, p) => `${p} range ${N} œufs dans des boîtes de ${d} œufs.`,
        libQ: 'Boîtes pleines', libR: 'Œufs hors des boîtes pleines',
        questions: [
            { cle: 'q', texte: 'Combien de boîtes seront pleines ?', suffixe: 'boîtes' },
            { cle: 'q1', texte: 'Combien de boîtes faut-il pour ranger tous les œufs ?', suffixe: 'boîtes' },
            { cle: 'r', texte: 'Combien d\'œufs restent hors des boîtes pleines ?', suffixe: 'œufs' },
        ],
    },
];

const tirerReste = () => {
    const d = randInt(2, 12);
    const r = randInt(1, d - 1);
    const q = randInt(2, Math.floor((200 - r) / d));
    const N = d * q + r;
    const ctx = randomChoice(CONTEXTES_RESTE);
    const p = randomChoice(PRENOMS);
    return { d, r, q, N, ctx, p, enonce: ctx.enonce(N, d, p) };
};

const reponseReste = (cle, { q, r }) => (cle === 'q' ? q : cle === 'q1' ? q + 1 : r);

const genDivisionResteTrou = () => {
    const t = tirerReste();
    const phrase = `${t.ctx.libQ} : {{${t.q}}}\n${t.ctx.libR} : {{${t.r}}}`;
    const texte = `<p>${t.enonce}</p>` +
        '<p>Complète avec le nombre de groupes complets, puis ce qui reste.</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_RESTE), mode: 'saisie' });
    return avecParams(payload, {
        niveau: 4, op: '÷', nombres: [t.N, t.d], reste: t.r, quotient: t.q,
        erreurVisee: 'division_toujours_exacte',
    });
};

/**
 * QCM : quotient, quotient + 1 ou reste selon la question ; les distracteurs
 * matérialisent le quotient nu (quotient_non_interprete), « la division
 * tombe juste » (quotient décimal arrondi au dixième : jamais entier car
 * 1/12 ≤ r/d ≤ 11/12), et le partage traité en soustraction (N − d). Toute
 * coïncidence de valeurs → nouveau tirage complet, jamais de bouche-trou.
 */
const genDivisionResteQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const t = tirerReste();
        const question = randomChoice(t.ctx.questions);
        const bonne = reponseReste(question.cle, t);
        const decimal = scaleToFr(Math.round((t.N / t.d) * 10), 1);
        const candidats = [];
        if (question.cle === 'q') candidats.push({ valeur: String(t.q + 1), code: 'quotient_non_interprete' });
        else candidats.push({ valeur: String(t.q), code: 'quotient_non_interprete' });
        candidats.push({ valeur: decimal, code: 'division_toujours_exacte' });
        candidats.push({ valeur: String(t.N - t.d), code: 'partage_confondu_soustraction' });
        const valeurs = [String(bonne), ...candidats.map(c => c.valeur)];
        if (new Set(valeurs).size !== valeurs.length) continue;
        const etiquette = (v) => `${v}${NB}${question.suffixe}`;
        const texte = `<p>${t.enonce}</p><p>${question.texte}</p>`;
        const payload = makeQcm(valeurs.map(etiquette), etiquette(bonne), texte, opts(L_RESTE));
        return avecParams(payload, {
            niveau: 4, op: '÷', nombres: [t.N, t.d], reste: t.r, quotient: t.q, question: question.cle,
            distracteurs: candidats.map(c => ({ valeur: etiquette(c.valeur), code: c.code })),
        });
    }
    throw new Error('division_reste_qcm : aucun tirage sans coïncidence en 40 essais');
};

const genDivisionResteSaisie = () => {
    const t = tirerReste();
    const question = randomChoice(t.ctx.questions);
    const bonne = reponseReste(question.cle, t);
    const texte = `<p>${t.enonce}</p><p>${question.texte} Écris un nombre entier.</p>`;
    const payload = makeSaisie(String(bonne), texte, { ...opts(L_RESTE), suffixe: question.suffixe });
    return avecParams(payload, {
        niveau: 4, op: '÷', nombres: [t.N, t.d], reste: t.r, quotient: t.q, question: question.cle,
        erreurVisee: question.cle === 'q' ? 'division_toujours_exacte' : 'quotient_non_interprete',
    });
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    calcul_nu_entiers_texte:        genCalculNuEntiers,
    calcul_nu_decimaux_texte:       genCalculNuDecimaux,
    choisir_operation_qcm_unique:   genChoixOperationQcm,
    choisir_operation_texte:        genChoixOperationSaisie,
    choisir_operation_expression:   genChoixOperationExpression,
    choisir_operation_tableau:      genChoixOperationTableau,
    associer_calcul_relier:         genAssocierRelier,
    associer_calcul_qcm_unique:     genAssocierQcm,
    associer_calcul_texte:          genAssocierSaisie,
    question_resoluble_qcm_unique:  genQuestionResolubleQcm,
    question_resoluble_texte:       genQuestionResolubleSaisie,
    division_reste_texte_trou_saisie: genDivisionResteTrou,
    division_reste_qcm_unique:      genDivisionResteQcm,
    division_reste_texte:           genDivisionResteSaisie,
};

export const CATEGORIES = [
    {
        id: 'calcul_nu', label: L_NU,
        formatsList: [
            { key: 'calcul_nu_entiers_texte',  label: 'Saisie (entiers)' },
            { key: 'calcul_nu_decimaux_texte', label: 'Saisie (décimaux)' },
        ],
    },
    {
        id: 'choisir_operation', label: L_CHOIX,
        formatsList: [
            { key: 'choisir_operation_qcm_unique', label: 'QCM' },
            { key: 'choisir_operation_texte',      label: 'Saisie' },
            { key: 'choisir_operation_expression', label: 'Écrire le calcul' },
            { key: 'choisir_operation_tableau',    label: 'Tableau' },
        ],
    },
    {
        id: 'associer_calcul', label: L_ASSOC,
        formatsList: [
            { key: 'associer_calcul_relier',     label: 'Relier' },
            { key: 'associer_calcul_qcm_unique', label: 'QCM' },
            { key: 'associer_calcul_texte',      label: 'Saisie' },
        ],
    },
    {
        id: 'question_resoluble', label: L_QRES,
        formatsList: [
            { key: 'question_resoluble_qcm_unique', label: 'QCM' },
            { key: 'question_resoluble_texte',      label: 'Saisie' },
        ],
    },
    {
        id: 'division_reste', label: L_RESTE,
        formatsList: [
            { key: 'division_reste_texte_trou_saisie', label: 'Quotient et reste' },
            { key: 'division_reste_qcm_unique',        label: 'QCM' },
            { key: 'division_reste_texte',             label: 'Saisie' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    scaleToFr, centToFr, dixToFr, tirerDecimalCent, ecrituresExpression,
    FAMILLES_OPERATION, STRUCTURES_ASSOC, THEMES_ASSOC, FAMILLES_QRES, CONTEXTES_RESTE,
    tirerCalculNuEntiers, tirerCalculNuDecimaux, tirerAssocier, tirerReste,
};
